import { useId, useMemo, useState } from 'react'
import { CheckIcon, Mtds, type OrdningsVerdier } from 'prove-common'

type ArchiveCategory = OrdningsVerdier[number]

type EditableArchiveCategoryFieldProps = {
  value?: number
  archiveCategories: OrdningsVerdier
  canEdit: boolean
  isSaving?: boolean
  onSave: (archiveCategoryId: number) => void | Promise<void>
}

export const EditableArchiveCategoryField = ({
  value,
  archiveCategories,
  canEdit,
  isSaving = false,
  onSave
}: EditableArchiveCategoryFieldProps) => {
  const popoverId = useId()
  const [search, setSearch] = useState('')
  const selected = archiveCategories.find((category) => category.id === value)

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return archiveCategories
    return archiveCategories.filter((category) =>
      category.kategoriNavn.toLowerCase().includes(query)
    )
  }, [archiveCategories, search])

  if (!canEdit) return <>{selected?.kategoriNavn ?? '-'}</>

  return (
    <>
      <Mtds.Button
        type='button'
        data-variant='tertiary'
        popoverTarget={popoverId}
        disabled={isSaving}
        className='-mx-1 h-auto min-h-0 justify-start p-1 font-normal'
      >
        {selected?.kategoriNavn ?? 'Velg arkivkategori'}
      </Mtds.Button>
      <Mtds.Popover id={popoverId} className='w-72 p-2'>
        <div className='grid gap-2'>
          <Mtds.Heading as='p' data-size='2xs'>
            Velg arkivkategori
          </Mtds.Heading>
          <Mtds.Field
            as='input'
            type='search'
            aria-label='Søk etter arkivkategori'
            placeholder='Søk...'
            value={search}
            onChange={({ target }) => setSearch(target.value)}
          />
          <menu className='grid max-h-72 gap-1 overflow-auto p-0'>
            {filtered.map((category: ArchiveCategory) => (
              <li key={category.id}>
                <Mtds.Button
                  type='button'
                  data-variant='tertiary'
                  popoverTargetAction='hide'
                  popoverTarget={popoverId}
                  onClick={() => {
                    if (category.id !== value) void onSave(category.id)
                    setSearch('')
                  }}
                  className='w-full justify-between font-normal'
                >
                  {category.kategoriNavn}
                  {category.id === value && <CheckIcon aria-hidden />}
                </Mtds.Button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li>
                <Mtds.Muted className='px-2 py-1'>Ingen treff</Mtds.Muted>
              </li>
            )}
          </menu>
        </div>
      </Mtds.Popover>
    </>
  )
}
